/**
 * ExploreCategoryCardLink - Promotional banner/card with image and link
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { useLocalized } from '../../Hooks/useLocalized';
import { useLocalizedImage } from '../../Hooks/useLocalizedMedia';
import { Theme } from '../../Theme/Theme';
import { MarkdownRenderer } from '../Markdown/MarkdownRenderer';
import { Container } from 'reactstrap';
import { getMediaLargeOrMediumOrSmallest, getMediaMedium } from '../../Model/Media/Media';

const Section = styled.div`
  padding: 24px 0;
`;

const CardContainer = styled.div`
  display: flex;
  align-items: stretch;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .arrow-icon {
      transform: translateX(4px);
    }

    .accent-line {
      width: 60px;
    }

    .image-container {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;

    &:hover {
      transform: translateY(-4px);
    }
  }
`;

const CardImage = styled.div<{ src?: string }>`
  width: 240px;
  min-height: 140px;
  flex-shrink: 0;
  background: ${({ src }) => (src ? `url(${src})` : 'linear-gradient(135deg, #00dda2 0%, #007763 100%)')};
  background-size: cover;
  background-position: center;
  position: relative;
  transition: transform 0.4s ease;

  @media (max-width: 768px) {
    width: 100%;
    height: 160px;
  }
`;

const AccentLine = styled.div`
  width: 40px;
  height: 3px;
  background: #00dda2;
  border-radius: 2px;
  margin-bottom: 12px;
  transition: width 0.3s ease;
`;

const CardContent = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ArrowIcon = styled.div`
  color: #00dda2;
  font-size: 20px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
`;

const Description = styled.div`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

export type ExploreCategoryCardLinkProps = {
  category: ExploreCategory;
  linkPrefix?: string;
};

export const ExploreCategoryCardLink: React.FC<ExploreCategoryCardLinkProps> = observer(
  ({ category, linkPrefix = '/' }) => {
    const title = useLocalized(category.name);
    const description = useLocalized(category.description);
    const image = useLocalizedImage(category.images);
    const imageUrl = image ? getMediaLargeOrMediumOrSmallest(image) : undefined;

    const rawLink = category.link?.trim() || `${linkPrefix}${category.identifier}`;
    
    // Check if it's an internal link (relative path or same domain)
    const isInternalLink = React.useMemo(() => {
      if (!rawLink.startsWith('http')) {
        return true; // Relative paths are internal
      }
      try {
        const url = new URL(rawLink);
        const currentHost = window.location.host;
        // Check if it's the same domain or a known internal domain
        return url.host === currentHost || 
               url.host === 'coach.kinastic.com' || 
               url.host.endsWith('.kinastic.com');
      } catch {
        return true; // If URL parsing fails, treat as internal
      }
    }, [rawLink]);

    // Extract path for internal links that are full URLs
    const link = React.useMemo(() => {
      if (isInternalLink && rawLink.startsWith('http')) {
        try {
          const url = new URL(rawLink);
          return url.pathname + url.search + url.hash;
        } catch {
          return rawLink;
        }
      }
      return rawLink;
    }, [rawLink, isInternalLink]);

    const content = (
      <CardContainer>
        {imageUrl && <CardImage className="image-container" src={imageUrl} />}
        <CardContent>
          <AccentLine className="accent-line" />
          <TitleRow>
            <Title>{title}</Title>
            <ArrowIcon className="arrow-icon">→</ArrowIcon>
          </TitleRow>
          {description && (
            <Description>
              <MarkdownRenderer text={description} />
            </Description>
          )}
        </CardContent>
      </CardContainer>
    );

    return (
      <Section>
        <Container>
          {isInternalLink ? (
            <StyledLink to={link}>{content}</StyledLink>
          ) : (
            <a href={link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
              {content}
            </a>
          )}
        </Container>
      </Section>
    );
  },
);
